import { useState, useCallback } from 'react';
import { PatCreateRequest, PatCreateResponse } from '@/services/identity/pat.service';
import {
  usePersonalAccessTokens,
  UsePersonalAccessTokensResult,
} from '../viewmodels/personal-access-tokens.viewmodel';

export interface UsePersonalAccessTokensUIResult extends UsePersonalAccessTokensResult {
  createModalOpen: boolean;
  displayModalOpen: boolean;
  pendingDisplayToken: PatCreateResponse | null;
  openCreateModal: () => void;
  closeCreateModal: () => void;
  openDisplayModal: (token: PatCreateResponse) => void;
  closeDisplayModal: () => void;
  handleCreateToken: (payload: PatCreateRequest) => Promise<PatCreateResponse>;
}

export const usePersonalAccessTokensUI = (): UsePersonalAccessTokensUIResult => {
  const patData = usePersonalAccessTokens();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [pendingDisplayToken, setPendingDisplayToken] = useState<PatCreateResponse | null>(null);

  const openCreateModal = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const openDisplayModal = useCallback((token: PatCreateResponse) => {
    setPendingDisplayToken(token);
    setDisplayModalOpen(true);
  }, []);

  const closeDisplayModal = useCallback(async () => {
    setPendingDisplayToken(null);
    setDisplayModalOpen(false);
    await patData.refresh();
  }, [patData]);

  const handleCreateToken = useCallback(
    async (payload: PatCreateRequest): Promise<PatCreateResponse> => {
      const token = await patData.create(payload);
      closeCreateModal();
      openDisplayModal(token);
      return token;
    },
    [patData, closeCreateModal, openDisplayModal],
  );

  return {
    ...patData,
    createModalOpen,
    displayModalOpen,
    pendingDisplayToken,
    openCreateModal,
    closeCreateModal,
    openDisplayModal,
    closeDisplayModal,
    handleCreateToken,
  };
};
